import { useEffect, useState } from "react"
import { Bot, Check, ChevronDown, ChevronRight, FileImage, FileText, FileVideo, Paperclip, ShieldCheck, User, Wrench, X } from "lucide-react"
import { cn } from "@/lib/cn"
import { MessageMarkdown } from "@/components/domain/message-markdown"
import type { ChatBubble } from "@/types/test_lab"

type Props = {
  msg: ChatBubble
  userLabel?: string
  assistantLabel?: string
  showToolCalls?: boolean
}

// Test-only conversation row. It intentionally mirrors the tuned ticket
// row-card language without importing TicketMessageCard, so Test Lab can
// keep tool calls visible while the production ticket surface stays stable.
export function TestConversationMessageCard({
  msg,
  userLabel = "User",
  assistantLabel = "Agent",
  showToolCalls = true,
}: Props) {
  if (msg.role === "internal_note") {
    return <InternalNoteRow msg={msg} />
  }

  const toolCalls   = Array.isArray(msg.toolCalls)   ? msg.toolCalls   as Array<Record<string, unknown>> : []
  const toolResults = Array.isArray(msg.toolResults) ? msg.toolResults as Array<Record<string, unknown>> : []
  const isToolOnly  = msg.role === "tool" || (msg.role === "assistant" && !msg.content && toolCalls.length > 0)

  if (isToolOnly) {
    return <ToolEventRow msg={msg} toolCalls={toolCalls} toolResults={toolResults} showToolCalls={showToolCalls} />
  }

  return (
    <RegularMessageRow
      msg={msg}
      userLabel={userLabel}
      assistantLabel={assistantLabel}
      showToolCalls={showToolCalls}
      toolCalls={toolCalls}
      toolResults={toolResults}
    />
  )
}

export function TestConversationThinkingRow({ label = "Agent" }: { label?: string }) {
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    const id = window.setInterval(() => setElapsed((s) => s + 1), 1000)
    return () => window.clearInterval(id)
  }, [])

  const subtitle = THINKING_TIERS.reduce((acc, t) => elapsed >= t.atSeconds ? t.subtitle : acc, THINKING_TIERS[0].subtitle)

  return (
    <article className="message-row rounded-xl px-4 py-2.5">
      <div className="flex gap-3">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-vip-gold/40 bg-vip-goldMute text-vip-goldSoft">
          <span className="text-xs">...</span>
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-baseline gap-2">
            <span className="font-semibold text-white">{label}</span>
            <span className="text-xs text-app-subtle">{subtitle}</span>
          </div>
          <div className="mt-2 flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-app-muted [animation-delay:0ms]" />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-app-muted [animation-delay:200ms]" />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-app-muted [animation-delay:400ms]" />
          </div>
        </div>
      </div>
    </article>
  )
}

function RegularMessageRow({
  msg,
  userLabel,
  assistantLabel,
  showToolCalls,
  toolCalls,
  toolResults,
}: {
  msg: ChatBubble
  userLabel: string
  assistantLabel: string
  showToolCalls: boolean
  toolCalls: Array<Record<string, unknown>>
  toolResults: Array<Record<string, unknown>>
}) {
  const isUser      = msg.role === "user"
  const isAssistant = msg.role === "assistant"
  const isHuman     = msg.role === "human_agent"
  const isSystem    = msg.role === "system"

  const label =
    msg.authorName?.trim()
      ? msg.authorName
      : isUser ? userLabel
      : isAssistant ? assistantLabel
      : isHuman ? "Human agent"
      : isSystem ? "System"
      : msg.role

  const labelClass = isHuman
    ? "font-semibold uppercase tracking-wide text-white"
    : "font-semibold text-white"
  const files = Array.isArray(msg.files) ? msg.files : []

  return (
    <article className={cn("rounded-xl px-4 py-2.5", isHuman ? "human-row" : "message-row")}>
      <div className="flex gap-3">
        <RoleAvatar msg={msg} label={label} />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-baseline gap-2">
            <span className={labelClass}>{label}</span>
            <span className="text-xs text-app-subtle">{formatTime(msg.createdAt)}</span>
          </div>
          {msg.content && (
            <div className="mt-0.5 text-sm leading-tight text-neutral-100">
              <MessageMarkdown content={msg.content} neutralizeLinks={isSystem} />
            </div>
          )}
          {files.length > 0 && (
            <div className="mt-2 flex flex-wrap items-start gap-2">
              {files.map((f, i) => (
                <FileChip key={`file-${i}`} file={f} />
              ))}
            </div>
          )}
          {showToolCalls && (toolCalls.length > 0 || toolResults.length > 0) && (
            <div className="mt-1.5 space-y-1">
              {pairCallsWithResults(toolCalls, toolResults).map((pair, i) => (
                <ToolPayloadCard
                  key={`tool-${i}`}
                  call={pair.call}
                  result={pair.result}
                  timestamp={msg.createdAt}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </article>
  )
}

function ToolEventRow({
  msg,
  toolCalls,
  toolResults,
  showToolCalls,
}: {
  msg: ChatBubble
  toolCalls: Array<Record<string, unknown>>
  toolResults: Array<Record<string, unknown>>
  showToolCalls: boolean
}) {
  const [open, setOpen] = useState(true)
  const pairs = pairCallsWithResults(toolCalls, toolResults)
  const head  = pairs[0]
  const name  = head?.call?.name ?? head?.result?.name ?? msg.content ?? "tool"
  const isErr = head?.result?.is_error === true
  const status: "running" | "succeeded" | "errored" =
    isErr ? "errored" : head?.result ? "succeeded" : "running"
  const StatusIcon = isErr ? X : status === "succeeded" ? Check : Wrench
  const statusTone = isErr
    ? "text-status-error"
    : status === "succeeded"
      ? "text-green-400"
      : "text-app-muted"

  return (
    <article className="tool-row rounded-xl px-4 py-2">
      <button
        type="button"
        disabled={!showToolCalls}
        onClick={() => showToolCalls && setOpen((o) => !o)}
        className={cn("flex w-full items-center gap-3 text-left", showToolCalls && "cursor-pointer")}
      >
        <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-white/10 bg-white/5 text-app-muted">
          <Wrench size={13} strokeWidth={2} />
        </div>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <span className="font-semibold text-white">Tool</span>
          <span className="text-xs text-app-subtle">{formatTime(msg.createdAt)}</span>
          <span className="h-3 w-px bg-white/10" />
          <span className={cn("inline-flex min-w-0 items-center gap-1 text-sm font-medium", statusTone)}>
            <StatusIcon size={13} strokeWidth={2.25} />
            <span className="truncate">{name}</span>
          </span>
          <span className="rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-app-muted">
            {status}
          </span>
        </div>
        {showToolCalls && (
          <span className="ml-auto shrink-0 text-app-subtle">
            {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          </span>
        )}
      </button>

      {showToolCalls && open && pairs.length > 0 && (
        <div className="mt-2 space-y-1 pl-10">
          {pairs.map((pair, i) => (
            <ToolPayloadCard
              key={`tool-${i}`}
              call={pair.call}
              result={pair.result}
              timestamp={msg.createdAt}
            />
          ))}
        </div>
      )}
    </article>
  )
}

function InternalNoteRow({ msg }: { msg: ChatBubble }) {
  return (
    <article className="internal-note-row rounded-xl px-4 py-2.5">
      <div className="flex gap-3">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-vip-gold/50 bg-vip-gold/10 text-vip-goldSoft">
          <ShieldCheck size={15} strokeWidth={2.5} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-vip-goldSoft">Internal note</span>
            <span className="text-xs uppercase tracking-wide text-app-muted">{msg.authorName ?? "Note"}</span>
            <span className="text-xs text-app-subtle">{formatTime(msg.createdAt)}</span>
          </div>
          <p className="mt-1 max-w-3xl whitespace-pre-wrap text-sm leading-5 text-[#ffe3a3]">
            {msg.content}
          </p>
        </div>
      </div>
    </article>
  )
}

function RoleAvatar({ msg, label }: { msg: ChatBubble; label: string }) {
  const isUser      = msg.role === "user"
  const isAssistant = msg.role === "assistant"
  const isHuman     = msg.role === "human_agent"
  const isSystem    = msg.role === "system"

  if (msg.avatarUrl) {
    return (
      <img
        src={msg.avatarUrl}
        alt={label}
        loading="lazy"
        className="h-9 w-9 shrink-0 rounded-full border border-white/10 object-cover"
      />
    )
  }

  const initial = label.trim()[0]?.toUpperCase()
  if (initial) {
    const tone = isUser
      ? "bg-[#ffdc5e] text-white"
      : isHuman
        ? "bg-gradient-to-br from-red-500 to-red-700 text-white"
        : isAssistant
          ? "border border-vip-gold/40 bg-vip-goldMute text-vip-goldSoft"
          : "border border-white/10 bg-white/5 text-app-muted"
    return (
      <div className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-full text-base font-bold", tone)}>
        {initial}
      </div>
    )
  }

  const Icon = isUser ? User : isHuman ? User : isSystem ? ShieldCheck : Bot
  return (
    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/10 bg-white/5 text-app-muted">
      <Icon size={16} strokeWidth={1.8} />
    </div>
  )
}

function FileChip({ file }: { file: NonNullable<ChatBubble["files"]>[number] }) {
  const mime = file.content_type || ""
  const Icon =
    mime.startsWith("video/") ? FileVideo :
    mime.startsWith("image/") ? FileImage :
    mime.startsWith("text/") || mime === "application/json" ? FileText :
    Paperclip
  const sizeLabel = formatBytes(file.byte_size)
  const meta = [file.content_type, sizeLabel].filter(Boolean).join(" · ")

  const inner = (
    <>
      <Icon size={14} className="shrink-0 text-app-muted" />
      <div className="min-w-0">
        <div className="truncate text-[12px] font-medium text-app-text">{file.filename}</div>
        {meta && <div className="truncate text-[10px] text-app-subtle">{meta}</div>}
      </div>
    </>
  )
  const className = "flex max-w-[260px] items-center gap-2 rounded-control border border-border bg-white/[0.04] px-2.5 py-1.5"

  if (file.url) {
    return (
      <a href={file.url} target="_blank" rel="noopener noreferrer" className={cn(className, "hover:bg-white/[0.07]")}>
        {inner}
      </a>
    )
  }

  return <div className={className} title="Test fixture - no URL available">{inner}</div>
}

type ToolCall   = { id?: string; name?: string; input?: unknown }
type ToolResult = { id?: string; name?: string; output?: unknown; is_error?: boolean }

function ToolPayloadCard({
  call,
  result,
  timestamp,
}: {
  call?: ToolCall
  result?: ToolResult
  timestamp: string
}) {
  const [open, setOpen] = useState(result?.is_error === true || !result)
  const name = call?.name ?? result?.name ?? "tool"
  const isErr = result?.is_error === true
  const status = result ? (isErr ? "errored" : "succeeded") : "called"
  const Icon = isErr ? X : result ? Check : Wrench
  const tone = isErr
    ? "border-status-error/30 bg-status-error/[0.07] text-status-error"
    : result
      ? "border-status-success/20 bg-status-success/[0.05] text-status-success"
      : "border-white/10 bg-white/[0.04] text-app-muted"
  const detail = primaryToolDetail(name, call?.input, result?.output)
  const request = requestMetadata(result?.output)
  const inputValue = call ? inputWithoutHeaderDetail(name, call.input ?? {}) : {}
  const outputValue = result ? displayToolOutput(name, result.output ?? {}) : {}

  return (
    <div className={cn("rounded-control border px-2 py-1.5 text-xs", tone)}>
      <button
        type="button"
        className="flex w-full items-center gap-1.5 text-left"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? <ChevronDown size={11} strokeWidth={2} /> : <ChevronRight size={11} strokeWidth={2} />}
        <Icon size={11} strokeWidth={2} />
        <span className="min-w-0 flex-1 truncate">
          <code>{name}</code>
          {detail && (
            <>
              <span className="text-app-subtle"> / </span>
              <code className="text-app-text">{detail}</code>
            </>
          )}
          <span className="text-app-subtle"> · {status}</span>
        </span>
        <span className="ml-auto shrink-0 text-[10px] text-app-subtle">
          {formatTime(timestamp)}
        </span>
      </button>

      {open && (
        <div className="mt-1.5 grid gap-1.5">
          {call && <PayloadPanel label="Input" value={inputValue} />}
          {result && (
            <PayloadPanel
              label={isErr ? "Error" : "Output"}
              value={outputValue}
            />
          )}
          {request && <RequestPanel request={request} />}
        </div>
      )}
    </div>
  )
}

function PayloadPanel({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="rounded-md border border-white/10 bg-black/20">
      <div className="border-b border-white/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-app-subtle">
        {label}
      </div>
      <pre className="max-h-64 overflow-auto px-2 py-1.5 text-[11px] leading-4 text-app-text">
        {payloadText(value)}
      </pre>
    </div>
  )
}

function RequestPanel({ request }: { request: RequestMetadata }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-md border border-white/10 bg-black/20">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 border-b border-white/10 px-2 py-1 text-left text-[10px] font-semibold uppercase tracking-wide text-app-subtle"
      >
        {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        <span>Request</span>
        {request.method && <span className="ml-1 normal-case tracking-normal text-app-muted">{request.method}</span>}
      </button>
      {open && (
        <pre className="max-h-64 overflow-auto px-2 py-1.5 text-[11px] leading-4 text-app-text">
          {payloadText(request)}
        </pre>
      )}
    </div>
  )
}

function payloadText(value: unknown): string {
  const clip = (text: string) => text.length > MAX_TOOL_PAYLOAD_CHARS
    ? `${text.slice(0, MAX_TOOL_PAYLOAD_CHARS)}\n... truncated ${text.length - MAX_TOOL_PAYLOAD_CHARS} chars`
    : text
  if (typeof value === "string") return clip(value)
  try {
    return clip(JSON.stringify(value ?? {}, null, 2))
  } catch {
    return clip(String(value))
  }
}

type RequestMetadata = {
  method?: string
  url?: string
  headers?: Record<string, string>
}

function requestMetadata(value: unknown): RequestMetadata | null {
  const out = asRecord(value)
  const direct = asRequestMetadata(out.request)
  if (direct) return direct

  const result = asRecord(out.result)
  return asRequestMetadata(result.request)
}

function asRequestMetadata(value: unknown): RequestMetadata | null {
  const request = asRecord(value)
  const url = stringish(request.url)
  const method = stringish(request.method)
  const headers = asRecord(request.headers)
  const headerStrings = Object.fromEntries(
    Object.entries(headers).map(([key, val]) => [key, String(val)]),
  )
  if (!url && !method && Object.keys(headerStrings).length === 0) return null

  return {
    ...(method ? { method } : {}),
    ...(url ? { url } : {}),
    ...(Object.keys(headerStrings).length > 0 ? { headers: headerStrings } : {}),
  }
}

function outputWithoutRequest(value: unknown): unknown {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value

  const out = { ...(value as Record<string, unknown>) }
  delete out.request
  if (out.result && typeof out.result === "object" && !Array.isArray(out.result)) {
    const result = { ...(out.result as Record<string, unknown>) }
    delete result.request
    out.result = result
  }
  return out
}

function inputWithoutHeaderDetail(name: string, value: unknown): unknown {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value

  const input = { ...(value as Record<string, unknown>) }
  if (name === "call_company_endpoint") {
    delete input.endpoint_key

    if (input.params && typeof input.params === "object" && !Array.isArray(input.params)) {
      const params = { ...(input.params as Record<string, unknown>) }
      delete params.endpoint_key
      delete params.endpoint
      if (Object.keys(params).length > 0) {
        input.params = params
      } else {
        delete input.params
      }
    }
  }

  return input
}

function displayToolOutput(name: string, value: unknown): unknown {
  const output = outputWithoutRequest(value)
  if (name !== "call_company_endpoint") return output

  return endpointResultOutput(output) ?? output
}

function endpointResultOutput(value: unknown): unknown | null {
  const out = asRecord(value)
  const result = asRecord(out.result)
  const status = result.status_code ?? result.status ?? out.status_code ?? out.status
  const body = result.upstream_body ?? result.body ?? out.upstream_body ?? out.body

  if (status == null && body == null) return null

  return {
    result: {
      ...(status != null ? { status } : {}),
      body: body ?? {},
    },
  }
}

function primaryToolDetail(name: string, input: unknown, output: unknown): string | null {
  const inObj = asRecord(input)
  const outObj = asRecord(output)

  if (name === "call_company_endpoint") {
    const params = asRecord(outObj.params)
    return stringish(inObj.endpoint_key) ?? stringish(params.endpoint) ?? stringish(outObj.endpoint)
  }

  return stringish(inObj.key) ?? stringish(inObj.kind) ?? stringish(outObj.key) ?? stringish(outObj.kind)
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
}

function stringish(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  return null
}

function pairCallsWithResults(
  calls: Array<Record<string, unknown>>,
  results: Array<Record<string, unknown>>,
): Array<{ call?: ToolCall; result?: ToolResult }> {
  const remainingResults = [...results] as ToolResult[]
  const pairs: Array<{ call?: ToolCall; result?: ToolResult }> = []

  for (const c of calls as ToolCall[]) {
    let matchIdx = -1
    if (c.id) matchIdx = remainingResults.findIndex((r) => r.id === c.id)
    if (matchIdx === -1) matchIdx = remainingResults.findIndex((r) => r.name === c.name)
    const match = matchIdx >= 0 ? remainingResults.splice(matchIdx, 1)[0] : undefined
    pairs.push({ call: c, result: match })
  }
  for (const orphan of remainingResults) pairs.push({ result: orphan })

  return pairs
}

function formatTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
}

function formatBytes(n: number | null | undefined): string {
  if (n == null || n <= 0) return ""
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

const THINKING_TIERS: { atSeconds: number; subtitle: string }[] = [
  { atSeconds:  0, subtitle: "thinking" },
  { atSeconds: 10, subtitle: "still thinking..." },
  { atSeconds: 20, subtitle: "hard problem" },
  { atSeconds: 30, subtitle: "harder problem" },
  { atSeconds: 40, subtitle: "real puzzler" },
  { atSeconds: 50, subtitle: "this one's a beast" },
  { atSeconds: 60, subtitle: "heavy lifting" },
  { atSeconds: 70, subtitle: "cracking it open" },
  { atSeconds: 80, subtitle: "almost have it" },
  { atSeconds: 90, subtitle: "any second now..." },
]

const MAX_TOOL_PAYLOAD_CHARS = 6000
